import React from 'react';
import { PlusCircle, Calendar, Send, Image, Palette, Pin, Link2, AlertTriangle, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuth = () => ({});

const StatCard = ({ label, value, icon: Icon, color, onClick }) => (
  <button onClick={onClick} className="bg-white rounded-2xl border border-stone-100 p-5 text-left hover:shadow-sm hover:border-stone-200 transition-all group" data-testid={`stat-${label.toLowerCase()}`}>
    <div className="flex items-center justify-between mb-3">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-4.5 h-4.5" />
      </div>
      <ArrowRight className="w-3.5 h-3.5 text-stone-300 group-hover:text-stone-500 transition-colors" />
    </div>
    <p className="font-sans text-2xl font-bold text-stone-800">{value}</p>
    <p className="font-sans text-xs text-stone-400 mt-0.5">{label}</p>
  </button>
);

const StudioOverview = ({ stats, pinterestStatus, brandKit, onNavigate, onEditPin, onRefresh }) => {
  const handleConnect = async () => {
    try {
      const res = await axios.get(`${API}/api/pinterest/auth`, getAuth());
      if (res.data.oauth_url) window.location.href = res.data.oauth_url;
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not start connection');
    }
  };

  return (
    <div className="space-y-6" data-testid="studio-overview">
      {/* Quick actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif font-bold text-stone-800 text-xl">Oversikt</h2>
          <p className="font-sans text-xs text-stone-400 mt-0.5">Ditt Pinterest-marketing center</p>
        </div>
        <Button
          data-testid="quick-create-btn"
          onClick={() => onNavigate('create')}
          className="bg-[#E60023] hover:bg-[#cc001f] text-white rounded-xl px-5 py-2.5 text-xs font-sans font-semibold"
        >
          <PlusCircle className="w-3.5 h-3.5 mr-1.5" /> Create new Pins
        </Button>
      </div>

      {/* Pinterest connection */}
      {!pinterestStatus?.connected && (
        <div className="bg-white rounded-2xl border border-stone-100 p-5" data-testid="connect-prompt">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-[#E60023]/10 flex items-center justify-center flex-shrink-0">
              <Pin className="w-5 h-5 text-[#E60023]" />
            </div>
            <div className="flex-1">
              <h3 className="font-sans font-bold text-stone-800 text-sm">Connect to Pinterest</h3>
              <p className="font-sans text-xs text-stone-500 mt-1 leading-relaxed">
                You can create, edit and save Pins without connecting. Connect to publish and schedule directly.
              </p>
              {!pinterestStatus?.configured && (
                <div className="flex items-center gap-1.5 mt-2 text-amber-600">
                  <AlertTriangle className="w-3 h-3" />
                  <span className="font-sans text-[10px] font-medium">Pinterest API ikke not configured yet</span>
                </div>
              )}
            </div>
            <Button
              onClick={handleConnect}
              disabled={!pinterestStatus?.configured}
              className="bg-[#E60023] hover:bg-[#cc001f] text-white rounded-xl px-4 py-2 text-xs font-sans font-semibold disabled:opacity-40 flex-shrink-0"
              data-testid="overview-connect-btn"
            >
              <Link2 className="w-3.5 h-3.5 mr-1.5" /> Koble til
            </Button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Drafts" value={stats?.drafts || 0} icon={Image} color="bg-stone-100 text-stone-600" onClick={() => onNavigate('pins')} />
        <StatCard label="Planlagt" value={stats?.scheduled || 0} icon={Calendar} color="bg-blue-50 text-blue-600" onClick={() => onNavigate('scheduled')} />
        <StatCard label="Published" value={stats?.published || 0} icon={Send} color="bg-green-50 text-green-600" onClick={() => onNavigate('published')} />
        <StatCard label="Totalt" value={stats?.total || 0} icon={Pin} color="bg-[#E60023]/10 text-[#E60023]" onClick={() => onNavigate('pins')} />
      </div>

      {/* Brand Kit quick check */}
      {!brandKit?.brand_name && (
        <div className="bg-white rounded-2xl border border-stone-100 p-5">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-[#B49A88]/10 flex items-center justify-center flex-shrink-0">
              <Palette className="w-5 h-5 text-[#B49A88]" />
            </div>
            <div className="flex-1">
              <h3 className="font-sans font-bold text-stone-800 text-sm">Set up Brand Kit</h3>
              <p className="font-sans text-xs text-stone-500 mt-1 leading-relaxed">
                Add logo, colors and fonts to automatically brand all Pins you create.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => onNavigate('brand')}
              className="rounded-xl px-4 py-2 text-xs font-sans font-semibold flex-shrink-0"
              data-testid="setup-brand-btn"
            >
              Set up
            </Button>
          </div>
        </div>
      )}

      {/* Quick start guide */}
      <div className="bg-white rounded-2xl border border-stone-100 p-6">
        <h3 className="font-serif font-bold text-stone-800 text-sm mb-4">Kom i gang</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { step: '1', title: 'Choose product', desc: 'Choose a product or start from scratch', action: 'Create Pins', onClick: () => onNavigate('create') },
            { step: '2', title: 'AI genererer', desc: 'Get 3-10 unique pins with different marketing angles', action: null },
            { step: '3', title: 'Publish', desc: 'Choose board and publish or schedule', action: null },
          ].map((s) => (
            <div key={s.step} className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-full bg-[#E60023] text-white text-xs font-bold flex items-center justify-center flex-shrink-0 font-sans">
                {s.step}
              </div>
              <div>
                <p className="font-sans font-semibold text-stone-700 text-xs">{s.title}</p>
                <p className="font-sans text-[10px] text-stone-400 mt-0.5 leading-relaxed">{s.desc}</p>
                {s.action && (
                  <button onClick={s.onClick} className="mt-2 text-[10px] font-sans font-semibold text-[#E60023] hover:underline">
                    {s.action} →
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StudioOverview;
